<template>
  <div class="data-project-basic-info">
    <el-form ref="formRef" :model="localForm" label-width="110px">
      <el-form-item label="项目名称" required>
        <el-input 
          v-model="localForm.name" 
          placeholder="请输入项目名称" 
          :disabled="readonlyName"
        />
      </el-form-item>

      <el-form-item v-if="showTemplate" label="数据模板" required>
        <el-cascader
          v-if="multipleTemplate"
          v-model="localForm.template_ids"
          :options="templateOptions"
          :props="{
            value: 'id',
            label: 'name',
            children: 'children',
            emitPath: false,
            multiple: true,
          }"
          placeholder="请选择数据模板（可多选）"
          clearable
          collapse-tags
          collapse-tags-tooltip
          style="width: 100%"
        />
        <el-cascader
          v-else
          v-model="localForm.template_id"
          :options="templateOptions"
          :props="{
            value: 'id',
            label: 'name',
            children: 'children',
            emitPath: false,
          }"
          placeholder="请选择数据模板"
          clearable
          style="width: 100%"
        />
        <div v-if="multipleTemplate" class="template-multi-tip">
          每选择一个模板，将创建一个独立的数据项目
        </div>
      </el-form-item>

      <el-form-item v-if="showOwner" label="项目负责人" required>
        <el-button
          type="primary"
          plain
          size="small"
          @click="openUserSelector('project_owner')"
        >
          {{
            localForm.project_owner_id
              ? getProjectOwnerName()
              : "选择项目负责人"
          }}
        </el-button>
      </el-form-item>

      <el-form-item label="项目类型" required>
        <el-radio-group 
          v-model="localForm.type" 
          @change="handleTypeChange"
        >
          <el-radio label="once">单次</el-radio>
          <el-radio label="periodic">周期</el-radio>
        </el-radio-group>
      </el-form-item>

      <el-form-item label="报送周期" required>
        <el-select
          v-model="localForm.period"
          placeholder="请选择报送周期"
          style="width: 200px"
          @change="handlePeriodChange"
        >
          <el-option label="按周" value="weekly" />
          <el-option label="按月" value="monthly" />
        </el-select>
        <div class="period-scope-tip">
          用于报表列表的周报 / 月报分类，单次项目也需选择
        </div>
      </el-form-item>

      <!-- 单次项目时间选择 -->
      <el-form-item v-if="localForm.type === 'once'" label="执行时间" required>
        <div class="period-time-container">
          <div class="date-range-container">
            <div class="date-picker-item">
              <div class="date-label">截止日期</div>
              <el-date-picker
                v-model="localForm.start_date"
                type="date"
                placeholder="选择执行日期"
                :disabled-date="timePickerOptions.start"
                value-format="YYYY-MM-DD"
              />
            </div>
          </div>
          <div class="time-picker-item">
            <div class="time-label">截止时间</div>
            <el-time-picker
              v-model="localForm.schedule_at"
              placeholder="选择执行时间"
              format="HH:mm"
              value-format="HH:mm"
            />
          </div>
        </div>
      </el-form-item>

      <!-- 周期项目时间选择 -->
      <el-form-item
        v-if="localForm.type === 'periodic'"
        label="执行周期"
        required
      >
        <div class="period-time-container">
          <div class="date-range-container">
            <div class="date-picker-item">
              <div class="date-label">开始日期</div>
              <el-date-picker
                v-model="localForm.start_date"
                type="date"
                placeholder="选择开始日期"
                :disabled-date="timePickerOptions.start"
                value-format="YYYY-MM-DD"
              />
            </div>
            <div class="date-picker-item">
              <div class="date-label">结束日期</div>
              <el-date-picker
                v-model="localForm.end_date"
                type="date"
                placeholder="选择结束日期"
                :disabled-date="timePickerOptions.end"
                value-format="YYYY-MM-DD"
              />
            </div>
          </div>
        </div>
      </el-form-item>

      <el-form-item
        v-if="localForm.type === 'periodic'"
        label="周期设置"
        required
      >
        <div class="period-settings-container">
          <div v-if="localForm.period === 'weekly'" class="period-options">
            <div class="option-title">选择星期几</div>
            <el-select
              v-model="localForm.week_days"
              placeholder="请选择星期几"
              style="width: 100%"
            >
              <el-option
                v-for="day in weekDaysOptions"
                :key="day.value"
                :label="day.label"
                :value="day.value"
              />
            </el-select>
          </div>

          <div v-if="localForm.period === 'monthly'" class="period-options">
            <div class="option-title">选择每月几号</div>
            <el-select
              v-model="localForm.month_days"
              placeholder="请选择日期"
              style="width: 100%"
            >
              <el-option
                v-for="day in monthDaysOptions"
                :key="day.value"
                :label="day.label"
                :value="day.value"
              />
            </el-select>
            <div v-if="showMonthDaysTip" class="month-days-tip">
              <i class="el-icon-info"></i>
              <span>若当月天数不足 {{ localForm.month_days }} 天，将自动调整为当月最后一天</span>
            </div>
          </div>
          <div v-show="localForm.period !== ''" class="period-options">
            <div class="time-label">截止时间</div>
            <el-time-picker
              v-model="localForm.schedule_at"
              placeholder="选择执行时间"
              format="HH:mm"
              value-format="HH:mm"
            />
          </div>
        </div>
      </el-form-item>

      <el-form-item
        v-if="showNextReportAt && localForm.type === 'periodic'"
        label="下次填报时间"
      >
        <div class="next-report-at-container">
          <el-date-picker
            v-model="localForm.next_report_at"
            type="datetime"
            placeholder="不设置则按周期调度"
            clearable
            format="YYYY-MM-DD HH:mm"
            value-format="YYYY-MM-DD HH:mm"
            :disabled-date="disablePastDate"
            :disabled-time="disablePastTime"
          />
          <div class="next-report-at-tip">
            设置后将覆盖周期调度，仅触发一次，触发后自动清空
          </div>
        </div>
      </el-form-item>

      <el-form-item v-if="showReporters" label="填报人" required>
        <div class="user-selector">
          <div class="selected-users">
            <el-tag
              v-for="(user, index) in localForm.reporters"
              :key="user.id"
              closable
              @close="removeReporter(index)"
            >
              {{ user.departmentName }} - {{ user.userName }}
            </el-tag>
          </div>
          <el-button
            type="primary"
            plain
            size="small"
            @click="openUserSelector('reporter')"
          >
            选择填报人
          </el-button>
        </div>
      </el-form-item>

      <!-- 添加关注人员选择 -->
      <el-form-item v-if="showWatchers" label="关注人员">
        <div class="user-selector">
          <div class="selected-users">
            <el-tag
              v-for="(user, index) in localForm.watchers"
              :key="user.id"
              closable
              @close="removeWatcher(index)"
            >
              {{ user.departmentName }} - {{ user.userName }}
            </el-tag>
          </div>
          <el-button
            type="primary"
            plain
            size="small"
            @click="openUserSelector('watcher')"
          >
            选择关注人员
          </el-button>
        </div>
      </el-form-item>
    </el-form>

    <DepartmentUserSelector
      ref="userSelectorRef"
      v-model:visible="showDialog"
      :departments="departments"
      :mode="getSelectorMode()"
      :limit-one-per-department="currentSelectorType === 'reporter'"
      @confirm="handleUserSelect"
    />
  </div>
</template>

<script>
import { getTemplatesGroupedByDepartment } from "@/api/report-system/templete";
import DepartmentUserSelector from "@/components/DepartmentUserSelector.vue";

export default {
  name: "DataProjectBasicInfo",
  components: {
    DepartmentUserSelector,
  },
  props: {
    modelValue: {
      type: Object,
      required: true,
      default: () => ({
        name: "",
        type: "",
        periodic: false,
        period: "weekly",
        week_days: "",
        month_days: "",
        start_date: null,
        schedule_at: null,
        end_date: null,
        next_report_at: "",
        template_id: null,
        project_owner_id: null,
        reporters: [],
        watchers: [],
        approvers: {
          level1: new Map(),
          level2: null,
          level3: null,
        },
      }),
    },
    departments: {
      type: Array,
      default: () => [],
    },
    readonlyName: {
      type: Boolean,
      default: false,
    },
    showTemplate: {
      type: Boolean,
      default: true,
    },
    // 新建项目时为 true：模板可多选，每选一个建一个项目。编辑已有项目时为 false。
    multipleTemplate: {
      type: Boolean,
      default: false,
    },
    showOwner: {
      type: Boolean,
      default: true,
    },
    showReporters: {
      type: Boolean,
      default: true,
    },
    showWatchers: {
      type: Boolean,
      default: true,
    },
    showNextReportAt: {
      type: Boolean,
      default: false,
    },
    allowPastDate: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      localForm: { ...this.modelValue },
      isInternalUpdate: false,
      templateOptions: [],
      showDialog: false,
      currentSelectorType: "",
      weekDaysOptions: [
        { value: "1", label: "星期一" },
        { value: "2", label: "星期二" },
        { value: "3", label: "星期三" },
        { value: "4", label: "星期四" },
        { value: "5", label: "星期五" },
        { value: "6", label: "星期六" },
        { value: "7", label: "星期日" },
      ],
      monthDaysOptions: [
        ...Array.from({ length: 31 }, (_, i) => ({
          value: (i + 1).toString(),
          label: `${i + 1}号`,
        })),
        {
          value: "32",
          label: "最后一天",
        },
      ],
      timePickerOptions: {
        start: (time) => {
          if (this.allowPastDate) return false;
          return time.getTime() < Date.now() - 8.64e7;
        },
        end: (time) => {
          return (
            time.getTime() <
            (this.localForm.start_date
              ? new Date(this.localForm.start_date).getTime()
              : Date.now() - 8.64e7)
          );
        },
      },
    };
  },
  computed: {
    showMonthDaysTip() {
      const day = parseInt(this.localForm.month_days);
      return day >= 29 && day <= 31;
    },
  },
  watch: {
    modelValue: {
      handler(val) {
        if (this.isInternalUpdate) {
          this.isInternalUpdate = false;
          return;
        }
        this.localForm = { ...val };
      },
      deep: true,
    },
    localForm: {
      handler(val) {
        this.isInternalUpdate = true;
        this.$emit("update:modelValue", val);
      },
      deep: true,
    },
  },
  created() {
    this.loadTemplates();
  },
  methods: {
    disablePastDate(time) {
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      return time.getTime() < todayStart.getTime();
    },
    disablePastTime(date) {
      if (!date) return {};
      const selected = new Date(date);
      const now = new Date();

      const isToday =
        selected.getFullYear() === now.getFullYear() &&
        selected.getMonth() === now.getMonth() &&
        selected.getDate() === now.getDate();

      if (!isToday) return {};

      const disabledHours = [];
      for (let h = 0; h < now.getHours(); h++) disabledHours.push(h);

      const disabledMinutes = (hour) => {
        if (hour !== now.getHours()) return [];
        const minutes = [];
        for (let m = 0; m <= now.getMinutes(); m++) minutes.push(m);
        return minutes;
      };

      return { disabledHours, disabledMinutes };
    },
    async loadTemplates() {
      try {
        const response = await getTemplatesGroupedByDepartment();
        const groupedTemplates = response.data;
        
        this.templateOptions = Object.entries(groupedTemplates).map(([department, templates]) => ({
          id: department,
          name: department,
          children: templates.map(template => ({
            id: template.id,
            name: template.name,
            template: template
          }))
        }));
      } catch (error) {
        console.error("加载模板列表失败:", error);
      }
    },
    handleTypeChange(val) {
      const newValue = { ...this.localForm };
      if (val !== "periodic") {
        // period 是所有项目共有的分类属性，切到单次时保留；
        // 只清掉调度用的星期几/几号与结束日期
        newValue.week_days = "";
        newValue.month_days = "";
        newValue.end_date = null;
      } else {
        if (!newValue.start_date) {
          newValue.start_date = new Date().toISOString().split("T")[0];
        }
      }
      this.localForm = newValue;
    },
    handlePeriodChange() {
      const newValue = { ...this.localForm };
      newValue.week_days = "";
      newValue.month_days = "";
      this.localForm = newValue;
    },
    openUserSelector(type) {
      this.currentSelectorType = type;
      this.$nextTick(() => {
        this.$refs.userSelectorRef?.clearAll();
      });
      this.showDialog = true;
    },
    getSelectorMode() {
      return this.currentSelectorType === "project_owner" ? "single" : "multiple";
    },
    getProjectOwnerName() {
      if (!this.localForm.project_owner_id || !this.departments.length) return "";
      for (const dept of this.departments) {
        const user = dept.users.find(u => u.id === this.localForm.project_owner_id);
        if (user) return `${dept.name} - ${user.name}`;
      }
      return "未知用户";
    },
    handleUserSelect(selectedUsers) {
      if (selectedUsers.length > 0) {
        const newValue = { ...this.localForm };
        
        if (this.currentSelectorType === "project_owner") {
          const user = selectedUsers[0];
          const dept = this.departments.find((d) => d.id === user.departmentId);
          const userInfo = dept.users.find((u) => u.id === user.userId);
          newValue.project_owner_id = userInfo.id;
        } else if (this.currentSelectorType === "watcher") {
          const newWatchers = [];
          for (const user of selectedUsers) {
            const dept = this.departments.find((d) => d.id === user.departmentId);
            const userInfo = dept.users.find((u) => u.id === user.userId);
            
            if (!newValue.watchers.some(w => w.id === userInfo.id)) {
              newWatchers.push({
                id: user.userId,
                departmentId: user.departmentId,
                departmentName: dept.name,
                userName: userInfo.name,
              });
            }
          }
          newValue.watchers = [...newValue.watchers, ...newWatchers];
        } else if (this.currentSelectorType === "reporter") {
          const newReporters = [];
          const existingDepartments = new Set(
            newValue.reporters.map((r) => r.departmentId)
          );

          for (const user of selectedUsers) {
            const dept = this.departments.find(
              (d) => d.id === user.departmentId
            );
            const userInfo = dept.users.find((u) => u.id === user.userId);

            if (existingDepartments.has(user.departmentId)) {
              continue;
            }

            newReporters.push({
              id: user.userId,
              departmentId: user.departmentId,
              departmentName: dept.name,
              userName: userInfo.name,
            });
            existingDepartments.add(user.departmentId);
          }
          newValue.reporters = [...newValue.reporters, ...newReporters];
        }
        
        this.localForm = newValue;
      }
      this.showDialog = false;
    },
    removeReporter(index) {
      const newValue = { ...this.localForm };
      const reporter = newValue.reporters[index];
      if (newValue.approvers && newValue.approvers.level1) {
         newValue.approvers.level1.delete(reporter.id);
      }
      newValue.reporters.splice(index, 1);
      this.localForm = newValue;
    },
    removeWatcher(index) {
      const newValue = { ...this.localForm };
      newValue.watchers.splice(index, 1);
      this.localForm = newValue;
    }
  }
};
</script>

<style scoped>
.period-time-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.date-range-container {
  display: flex;
  gap: 20px;
}

.date-picker-item,
.time-picker-item {
  flex: 1;
}

.date-label,
.time-label,
.option-title {
  font-size: 13px;
  color: #606266;
  margin-bottom: 8px;
}

.period-settings-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.period-options {
  margin-top: 5px;
}

.month-days-tip {
  margin-top: 8px;
  font-size: 12px;
  color: #909399;
  display: flex;
  align-items: center;
  gap: 4px;
}

.month-days-tip i {
  font-size: 12px;
  color: #909399;
}

.period-scope-tip {
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
}

.template-multi-tip {
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
}

.next-report-at-container {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.next-report-at-tip {
  font-size: 12px;
  color: #909399;
  line-height: 1.2;
}

.user-selector {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}

.selected-users {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  min-height: 32px;
  padding: 3px 6px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background-color: #fff;
  line-height: 1.5;
}

.selected-users:hover {
  border-color: #c0c4cc;
}

.selected-users:focus-within {
  border-color: #409eff;
}
</style>
